import getSymbolFromCurrency from 'currency-symbol-map';
import type { TFunction } from 'i18next';
import moment from 'moment';
import { NUMBER } from '../../../constant/number.constant';
import { i18n } from '../../../services/translation/i18n';
import {
  DurationUnit,
  FlatFeeTypes,
  PricingType,
  ScheduleType,
  SessionLimitTypes,
} from '../../../stores/types/price.interface';
import { format2Digits, formatSessionTimeLimitHours } from '../../../util/numbers-utility';
import { sessionPricingType } from '../charger-session/utils';
import { FlatFee } from '../dto/charger.dto';
// getPricingType convert selected price unit key into view string

export const getPricingType = (type: string) => {
  let response;
  switch (type) {
    case PricingType.BILLED_BY_EFFECTIVE_CHARGING_TIME:
      response = i18n.t('price_charging_label');
      break;
    case PricingType.BILLED_BY_TIME_PLUGGED_IN:
      response = i18n.t('receipt_plugged_in');
      break;
    case PricingType.BILLED_BY_KWH:
      response = '';
      break;
    default:
      response = i18n.t('receipt_plugged_in');
      break;
  }
  return response;
};

// getDurationCount it modifies tier duration into view string
// Converts duration based on durationUnit in either hour or kilowatthours
const convertDuration = (duration: number, durationUnit: string) => {
  if (durationUnit === DurationUnit.SECONDS) {
    return duration / 3600;
  }
  if (durationUnit === DurationUnit.JOULES) {
    return duration / 3600000;
  }
  return duration;
};

const isApproximatelyOne = (value: number) => Math.abs(value - 1) < 1e-6;

/**
 * Renders tier duration labels (hours or kWh) with full-phrase i18n + interpolation
 * so word order and grammar are correct for each language.
 */
export const getDurationCount = (
  duration: number,
  _pricingType: string,
  index: number,
  durationUnit: string,
  t: TFunction,
) => {
  if (!duration) {
    return t('afterwards');
  }

  const isFirst = index === 0;
  const isKwh = durationUnit === DurationUnit.JOULES;
  const amount = convertDuration(duration, durationUnit);

  if (isKwh) {
    if (isFirst) {
      return t('for_first_kwh', { amount });
    }
    return t('for_next_kwh', { amount });
  }

  if (isApproximatelyOne(amount)) {
    return t(isFirst ? 'for_first_hour' : 'for_next_hour');
  }

  if (isFirst) {
    return t('for_first_hours', { amount });
  }

  return t('for_next_hours', { amount });
};

//convertRateUnits converts HOUR => hr and KWH => Kwh

const convertRateUnits = (unit: string) => {
  let response = 'hr';
  if (unit === PricingType.BILLED_BY_KWH) {
    response = 'kWh';
  }
  return response;
};

// change '$1.0' => '$1.00'
export const formatPrice = (price: number, currency: string) => {
  if (price === 0) return '$0.00';
  return `${getSymbolFromCurrency(currency)}${format2Digits(price)}`;
};

// getRateCount show price and duration unit into view with pricingType

export const getRateCount = (price: number, pricingType: string, currency: string) => {
  return `${formatPrice(price, currency)}/${convertRateUnits(
    pricingType,
  )} ${getPricingType(pricingType)}`;
};

export const getRateCountWithoutType = (price: number, currency: string) => {
  return `${formatPrice(price, currency)}`;
};
export const getTimeinAMPMFormat = (time: string) => {
  const startTime = moment(time, 'YYYY-MM-DDTHH:mm:ssZ').toDate();
  return moment(startTime).format('hh:mm A');
};

// sort pricing accoding to weekdays

export const weekDays = {
  Mon: 1,
  Tue: 2,
  Wed: 3,
  Thu: 4,
  Fri: 5,
  Sat: 6,
  Sun: 7,
};

//Convert 24 hour format to 12 hour format
export const formatTime = (time: string) => {
  return moment(time, 'HH:mm').format('h:mm A');
};

export const mergeDayPricing = (days) => {
  const obj = {};

  days.forEach((element) => {
    obj[element.day] = element.pricePeriods;
  });

  const data = Object.keys(weekDays).map((item) => {
    if (obj[item.toUpperCase()]) {
      return { day: item, pricePeriods: obj[item.toUpperCase()] };
    } else {
      return { day: item, pricePeriods: i18n.t('free') };
    }
  });

  return data;
};

export const convertDaysArrayToString = (days) => {
  return days.map((day) => day.substring(0, 3)).join(', ');
};

export const formatDays = (day: string, type: string) => {
  if (type === ScheduleType.WEEKDAYS_WEEKEND) {
    return day === 'MON_FRI' ? i18n.t('weekday') : i18n.t('weekends');
  }
  if (type === ScheduleType.SAME_EVERYDAY) {
    return i18n.t('daily');
  }
  return day;
};

export const formatPricingTime = (startTime: string, endTime: string) => {
  if (moment(endTime, 'HH:mm').diff(moment(startTime, 'HH:mm'), 'hours') === NUMBER.TWENTY_FOUR) {
    return i18n.t('all_day');
  }

  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
};

export const getIdleRate = (price: number, currency: string) => {
  return `${getRateCountWithoutType(price, currency)}/${sessionPricingType(PricingType.IDLE)}`;
};

export const getSessionLimit = (limit: number, type: SessionLimitTypes) => {
  if (type === SessionLimitTypes.TIME_LIMIT) {
    var timeLimit = formatSessionTimeLimitHours(limit);
    return `${timeLimit} ${limit <= 60 ? i18n.t('hour') : i18n.t('hours')}`;
  } else {
    return '';
  }
};

// Get session fee for display (simple format: $0.75)
export const getSessionFee = (amount: number, currency: string) => {
  return getRateCountWithoutType(amount, currency); // Uses existing $X.XX format
};

// Extract session fees from flat fees array
export const getSessionFees = (flatFees?: FlatFee[]): FlatFee[] => {
  return flatFees?.filter((fee) => fee.type === FlatFeeTypes.SESSION_FEE) || [];
};

// Get total session fee amount (sum of all session fees)
export const getSessionFeeAmount = (flatFees?: FlatFee[]): number => {
  const sessionFees = getSessionFees(flatFees);
  return sessionFees.reduce((total, fee) => total + fee.amount, 0);
};

// Check if charger has session fees
export const hasSessionFees = (flatFees?: FlatFee[]): boolean => {
  return getSessionFees(flatFees).length > 0;
};

// Generic helpers to work with both active session cost breakdown items and receipt line items
// Determine pricing type from either shape
export const getItemPricingType = (item: any): string => {
  return (item && (item.pricingType ?? item.unitPricingType)) || '';
};

// Check if an item is a session fee (flat rate)
export const isSessionFeeItem = (item: any): boolean => {
  return getItemPricingType(item) === PricingType.FLAT_RATE;
};

// Check if an item is an idle fee
export const isIdleItem = (item: any): boolean => {
  return getItemPricingType(item) === PricingType.IDLE;
};

// Check if an item is a charging fee (neither idle nor flat rate)
export const isChargingItem = (item: any): boolean => {
  const type = getItemPricingType(item);
  return type !== PricingType.IDLE && type !== PricingType.FLAT_RATE;
};

export const getTotalPrice = (values: number[]): number => {
  return values.reduce((total, value) => total + value, 0);
};
